import { useEffect, useState } from "react";
import { GripVertical, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { ImageInput } from "@/components/ImageInput";

const TREATMENT_CATEGORIES = [
  "Skin & Facial Aesthetics",
  "Hair Restoration",
  "Weight Management",
  "IV Wellness & Drips",
  "Facials & Skin Wellness",
  "Laser Hair Reduction",
];

export const COLLECTION_CONFIGS = {
  treatments: {
    singular: "Treatment",
    title: (i) => i.name,
    subtitle: (i) => `${i.category} · AED ${i.price}`,
    fields: [
      { name: "name", label: "Name", type: "text" },
      { name: "category", label: "Category", type: "select", options: TREATMENT_CATEGORIES },
      { name: "tagline", label: "Tagline", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "duration", label: "Duration (e.g. 60 min)", type: "text" },
      { name: "downtime", label: "Downtime (e.g. None)", type: "text" },
      { name: "price", label: "Price (AED)", type: "number" },
      { name: "image", label: "Image URL", type: "image" },
      { name: "benefits", label: "Benefits (comma separated)", type: "list" },
      { name: "featured", label: "Featured on homepage", type: "boolean" },
    ],
  },
  doctors: {
    singular: "Doctor",
    title: (i) => i.name,
    subtitle: (i) => i.role,
    fields: [
      { name: "name", label: "Full Name", type: "text" },
      { name: "role", label: "Role / Title", type: "text" },
      { name: "experience", label: "Experience (e.g. 10+ years)", type: "text" },
      { name: "image", label: "Portrait Image URL", type: "image" },
      { name: "bio", label: "Bio", type: "textarea" },
      { name: "specializations", label: "Specializations (comma separated)", type: "list" },
      { name: "certifications", label: "Certifications (comma separated)", type: "list" },
    ],
  },
  gallery: {
    singular: "Gallery Item",
    title: (i) => i.title,
    subtitle: (i) => i.category,
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "category", label: "Category", type: "select", options: ["Results", "Clinic", "Treatments"] },
      { name: "image", label: "Image URL", type: "image" },
      { name: "before_image", label: "Before Image URL (optional — enables comparison slider)", type: "image" },
      { name: "after_image", label: "After Image URL (optional)", type: "image" },
      { name: "description", label: "Description", type: "textarea" },
    ],
  },
  testimonials: {
    singular: "Testimonial",
    title: (i) => i.name,
    subtitle: (i) => i.treatment,
    fields: [
      { name: "name", label: "Client Name", type: "text" },
      { name: "role", label: "Client Detail (e.g. Client for 2 years)", type: "text" },
      { name: "treatment", label: "Treatment Received", type: "text" },
      { name: "rating", label: "Rating (1-5)", type: "number" },
      { name: "text", label: "Review Text", type: "textarea" },
    ],
  },
  packages: {
    singular: "Package",
    title: (i) => i.name,
    subtitle: (i) => `${i.price} ${i.period}`,
    fields: [
      { name: "name", label: "Package Name", type: "text" },
      { name: "price", label: "Price (e.g. AED 1,099)", type: "text" },
      { name: "period", label: "Period (e.g. per month)", type: "text" },
      { name: "features", label: "Features (comma separated)", type: "list" },
      { name: "highlighted", label: "Highlight as Most Loved", type: "boolean" },
      { name: "cta", label: "Button Label", type: "text" },
    ],
  },
  articles: {
    singular: "Article",
    title: (i) => i.title,
    subtitle: (i) => `${i.category} · ${i.read_time}`,
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "category", label: "Category", type: "select", options: ["Skin", "Hair", "Wellness", "Injectables"] },
      { name: "cluster", label: "Topic Cluster (e.g. Skin Rejuvenation — groups related essays)", type: "text" },
      { name: "slug", label: "URL Slug (empty = auto from title)", type: "text" },
      { name: "seo_title", label: "SEO Title (50–60 chars, empty = title)", type: "text" },
      { name: "seo_description", label: "SEO Description (150–160 chars, empty = excerpt)", type: "textarea" },
      { name: "excerpt", label: "Excerpt (shown on cards)", type: "textarea" },
      { name: "image", label: "Cover Image URL", type: "image" },
      { name: "author", label: "Author", type: "text" },
      { name: "read_time", label: "Read Time (e.g. 6 min read)", type: "text" },
      { name: "published_at", label: "Publish Date (YYYY-MM-DD)", type: "text" },
      { name: "content", label: "Article Body (blank line between paragraphs)", type: "paragraphs" },
    ],
  },
  faqs: {
    singular: "FAQ",
    title: (i) => i.question,
    subtitle: (i) => (i.answer || "").slice(0, 60),
    fields: [
      { name: "question", label: "Question", type: "text" },
      { name: "answer", label: "Answer", type: "textarea" },
    ],
  },
  seo_keywords: {
    singular: "Keyword Rule",
    title: (i) => (i.keyword_list || [])[0] || "Keyword rule",
    subtitle: (i) => `→ ${i.target_url || ""}`,
    fields: [
      { name: "keyword_list", label: "Keywords & synonyms (comma separated)", type: "list" },
      { name: "target_url", label: "Links To (e.g. /treatments or /blog/my-guide)", type: "text" },
    ],
  },
  custom_tags: {
    singular: "Custom Tag",
    title: (i) => i.name,
    subtitle: (i) => `${i.location === "body" ? "After <body>" : "Inside <head>"} · ${i.enabled === "off" ? "Paused" : "Active"}`,
    fields: [
      { name: "name", label: "Tag Name (e.g. Meta Pixel)", type: "text" },
      { name: "location", label: "Inject Into", type: "select", options: [{ value: "head", label: "<head> (most tags)" }, { value: "body", label: "Start of <body>" }] },
      { name: "code", label: "Tag Code (full <script>…</script> snippet)", type: "textarea" },
      { name: "enabled", label: "Status", type: "select", options: [{ value: "on", label: "Active" }, { value: "off", label: "Paused" }] },
    ],
  },
  instagram_posts: {
    singular: "Instagram Post",
    title: (i) => (i.caption || "Post").slice(0, 42),
    subtitle: (i) => i.link || "",
    fields: [
      { name: "image", label: "Image URL", type: "image" },
      { name: "caption", label: "Caption", type: "textarea" },
      { name: "link", label: "Post Link (Instagram URL)", type: "text" },
    ],
  },
};

export const CollectionTab = ({ collection }) => {
  const cfg = COLLECTION_CONFIGS[collection];
  const { content, refresh } = useContent();
  // articles list comes from the admin endpoint so DRAFTS stay visible here
  // (public /content hides them)
  const [adminArticles, setAdminArticles] = useState(null);
  const fetchAdmin = async () => {
    if (collection !== "articles") return;
    try {
      const { data } = await api.get("/admin/articles");
      setAdminArticles(data);
    } catch {}
  };
  useEffect(() => {
    fetchAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);
  const items = collection === "articles" ? adminArticles || [] : content?.[collection] || [];
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [reordering, setReordering] = useState(false);

  const reload = async () => {
    await Promise.all([refresh(), fetchAdmin()]);
  };

  const publish = async (item) => {
    try {
      await api.put(`/admin/articles/${item.id}`, { draft: false });
      await reload();
      toast.success(`"${cfg.title(item)}" is live on the Journal`);
    } catch {
      toast.error("Publish failed — please try again");
    }
  };

  const openNew = () => {
    const f = {};
    cfg.fields.forEach((fd) => {
      f[fd.name] = fd.type === "boolean" ? "false" : fd.type === "select" ? fd.options[0] : "";
    });
    setForm(f);
    setEditing("new");
  };

  const openEdit = (item) => {
    const f = {};
    cfg.fields.forEach((fd) => {
      const v = item[fd.name];
      if (fd.type === "list") f[fd.name] = (v || []).join(", ");
      else if (fd.type === "paragraphs") f[fd.name] = (v || []).join("\n\n");
      else if (fd.type === "boolean") f[fd.name] = v ? "true" : "false";
      else f[fd.name] = v ?? "";
    });
    setForm(f);
    setEditing(item);
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    cfg.fields.forEach((fd) => {
      let v = form[fd.name];
      if (fd.type === "list") v = String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
      else if (fd.type === "paragraphs") v = String(v || "").split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
      else if (fd.type === "boolean") v = v === "true" || v === true;
      else if (fd.type === "number") v = Number(v) || 0;
      payload[fd.name] = v;
    });
    try {
      if (editing === "new") await api.post(`/admin/${collection}`, payload);
      else await api.put(`/admin/${collection}/${editing.id}`, payload);
      await reload();
      toast.success(`${cfg.singular} saved`);
      setEditing(null);
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${cfg.title(item)}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${collection}/${item.id}`);
      await reload();
      toast.success(`${cfg.singular} deleted`);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const onDropRow = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx || reordering) {
      setDragIdx(null);
      return;
    }
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(dragIdx, 1);
    ids.splice(targetIdx, 0, moved);
    setDragIdx(null);
    setReordering(true);
    try {
      await api.post(`/admin/${collection}/reorder`, { ids });
      await reload();
      toast.success("Placement updated — live on the website");
    } catch (e) {
      toast.error("Reorder failed");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid={`admin-${collection}-tab`}>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold capitalize text-[#1A1615]">{collection === "articles" ? "Journal" : collection}</h2>
        <button onClick={openNew} data-testid={`admin-${collection}-add-button`} className="btn-gold px-5 py-2.5 text-xs">
          <Plus className="h-4 w-4" aria-hidden="true" /> Add {cfg.singular}
        </button>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs font-light text-[#91837C]">
        <GripVertical className="h-3.5 w-3.5" aria-hidden="true" /> Drag any row to change its placement on the website — saves automatically.
      </p>

      <div className="mt-4 space-y-3">
        {items.map((item, idx) => (
          <div
            key={item.id}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDropRow(idx)}
            onDragEnd={() => setDragIdx(null)}
            className={`glass flex items-center gap-3 rounded-2xl p-4 transition-[opacity,box-shadow] duration-300 ${
              dragIdx === idx ? "opacity-40 shadow-[0_0_0_2px_#D4AF37]" : ""
            }`}
            data-testid={`admin-${collection}-item-${item.id}`}
          >
            <span
              className="cursor-grab touch-none text-[#C9BBB2] transition-colors hover:text-[#B38B2D] active:cursor-grabbing"
              data-testid={`admin-${collection}-drag-${item.id}`}
              aria-label={`Drag to reorder ${cfg.title(item)}`}
              title="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" aria-hidden="true" />
            </span>
            {item.image && (
              <img src={item.image} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" loading="lazy" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#1A1615]">
                {collection === "articles" && item.draft && (
                  <span className="mr-2 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-amber-700">Draft</span>
                )}
                {cfg.title(item)}
              </p>
              <p className="truncate text-xs text-[#91837C]">{cfg.subtitle(item)}</p>
            </div>
            {collection === "articles" && item.draft && (
              <button
                onClick={() => publish(item)}
                data-testid={`admin-articles-publish-${item.id}`}
                className="btn-gold shrink-0 px-4 py-2 text-[10px]"
              >
                Publish
              </button>
            )}
            <button
              onClick={() => openEdit(item)}
              data-testid={`admin-${collection}-edit-${item.id}`}
              aria-label={`Edit ${cfg.title(item)}`}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#B38B2D] transition-colors hover:bg-[#D4AF37]/10"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              onClick={() => remove(item)}
              data-testid={`admin-${collection}-delete-${item.id}`}
              aria-label={`Delete ${cfg.title(item)}`}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">Nothing here yet — add your first {cfg.singular.toLowerCase()}.</p>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/60 bg-[#FFFDF9]/95 backdrop-blur-xl sm:max-w-xl" data-testid={`admin-${collection}-editor`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-semibold text-[#1A1615]">
              {editing === "new" ? `New ${cfg.singular}` : `Edit ${cfg.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cfg.fields.map((fd) => (
              <div key={fd.name}>
                <label htmlFor={`field-${fd.name}`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
                  {fd.label}
                </label>
                {fd.type === "textarea" || fd.type === "paragraphs" ? (
                  <textarea id={`field-${fd.name}`} rows={fd.type === "paragraphs" ? 8 : 4} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`admin-field-${fd.name}`} className="input-lux resize-none" />
                ) : fd.type === "select" ? (
                  <select id={`field-${fd.name}`} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`admin-field-${fd.name}`} className="input-lux">
                    {fd.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : fd.type === "boolean" ? (
                  <select id={`field-${fd.name}`} value={form[fd.name] || "false"} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`admin-field-${fd.name}`} className="input-lux">
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : fd.type === "image" ? (
                  <ImageInput value={form[fd.name] || ""} onChange={(v) => setForm({ ...form, [fd.name]: v })} testid={`admin-field-${fd.name}`} />
                ) : (
                  <input id={`field-${fd.name}`} type={fd.type === "number" ? "number" : "text"} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`admin-field-${fd.name}`} className="input-lux" />
                )}
              </div>
            ))}
            <button onClick={save} disabled={saving} data-testid={`admin-${collection}-save-button`} className="btn-gold w-full px-6 py-3.5 text-sm disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
